import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  UserIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClipboardDocumentCheckIcon,
  HandThumbUpIcon
} from '@heroicons/react/24/outline';
import {
  ShoppingBagIcon as ShoppingBagIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  TruckIcon as TruckIconSolid,
  ClockIcon as ClockIconSolid,
  ClipboardDocumentCheckIcon as ClipboardCheckSolid
} from '@heroicons/react/24/solid';
import { ordersAPI, getImageUrl } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

const AdminOrders = () => {
  const [ordersByStatus, setOrdersByStatus] = useState({
    paidOrders: [],
    receivedOrders: [],
    inTransitOrders: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionData, setActionData] = useState({
    notes: '',
    trackingNumber: '',
    shippingCompany: ''
  });
  const [processingAction, setProcessingAction] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);

  // قائمة شركات الشحن
  const shippingCompanies = [
    { id: 'aramex', name: 'أرامكس', nameEn: 'Aramex' },
    { id: 'smsa', name: 'سمسا إكسبرس', nameEn: 'SMSA Express' },
    { id: 'dhl', name: 'دي إتش إل', nameEn: 'DHL' },
    { id: 'fedex', name: 'فيديكس', nameEn: 'FedEx' },
    { id: 'ups', name: 'يو بي إس', nameEn: 'UPS' },
    { id: 'naqel', name: 'ناقل إكسبرس', nameEn: 'Naqel Express' },
    { id: 'zajil', name: 'زاجل', nameEn: 'Zajil' },
    { id: 'other', name: 'أخرى', nameEn: 'Other' }
  ];

  // Load orders by status
  useEffect(() => {
    loadOrdersByStatus();
  }, []);

  const loadOrdersByStatus = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getOrdersByStatus();
      setOrdersByStatus(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  // Handle view order details
  const handleViewOrder = async (order) => {
    try {
      const response = await ordersAPI.getById(order._id);
      setSelectedOrder(response.data.data);
      setShowDetailsModal(true);
      } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تحميل تفاصيل الطلب');
    }
  };

  // Handle action button click
  const handleAction = (order, type) => {
    setSelectedOrder(order);
    setActionType(type);
    setActionData({ notes: '', trackingNumber: '', shippingCompany: '' });
    setShowActionModal(true);
  };

  // Handle cancel order
  const handleCancelOrder = async (order) => {
    // منع التكرار إذا كان الطلب قيد المعالجة
    if (cancellingOrder === order._id) {
      return;
    }

    const reason = window.prompt('يرجى كتابة سبب الإلغاء:');
    if (!reason || reason.trim().length < 5) {
      toast.error('سبب الإلغاء مطلوب (5 أحرف على الأقل)');
      return;
    }

    if (!window.confirm(`هل أنت متأكد من إلغاء الطلب ${order.orderNumber}؟`)) {
      return;
    }

    try {
      setCancellingOrder(order._id);
      await ordersAPI.cancelByAdmin(order._id, reason.trim());
      toast.success('تم إلغاء الطلب بنجاح');
      setShowDetailsModal(false);
      loadOrdersByStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في إلغاء الطلب');
    } finally {
      setCancellingOrder(null);
    }
  };

  // Submit action
  const handleActionSubmit = async () => {
    if (!selectedOrder || !actionType || processingAction) return;

    // التحقق من اختيار شركة الشحن عند بدء الشحن
    if (actionType === 'in_transit' && !actionData.shippingCompany) {
      toast.error('يجب اختيار شركة الشحن');
      return;
    }

    try {
      setProcessingAction(true);
      let response;
      const data = {
        notes: actionData.notes
      };

      if (actionType === 'in_transit') {
        if (actionData.trackingNumber) {
          data.trackingNumber = actionData.trackingNumber;
        }
        if (actionData.shippingCompany) {
          data.shippingCompany = actionData.shippingCompany;
        }
      }

      switch (actionType) {
        case 'received':
          response = await ordersAPI.markAsReceived(selectedOrder._id, data);
          break;
        case 'in_transit':
          response = await ordersAPI.markAsInTransit(selectedOrder._id, data);
          break;
        case 'delivered':
          response = await ordersAPI.markAsDelivered(selectedOrder._id, data);
          break;
        default:
          throw new Error('نوع الإجراء غير صحيح');
      }

      toast.success(response.data?.message || 'تم تحديث حالة الطلب بنجاح');
      setShowActionModal(false);
      setShowDetailsModal(false);
      loadOrdersByStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تحديث حالة الطلب');
    } finally {
      setProcessingAction(false);
    }
  };

  // Get status text
  const getStatusText = (status) => {
    const statusTexts = {
      pending: 'في الانتظار',
      paid: 'تم الدفع',
      admin_received: 'تم الاستلام',
      in_transit: 'في الطريق',
      delivered: 'تم التسليم',
      completed: 'مكتمل',
      cancelled: 'ملغي',
      refunded: 'مُسترد'
    };
    return statusTexts[status] || status;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'yellow', text: 'في الانتظار', icon: ClockIcon },
      paid: { color: 'blue', text: 'تم الدفع - ينتظر الاستلام', icon: ClockIconSolid },
      admin_received: { color: 'indigo', text: 'تم الاستلام - جاري الشحن', icon: ClipboardCheckSolid },
      in_transit: { color: 'purple', text: 'في الطريق', icon: TruckIconSolid },
      delivered: { color: 'emerald', text: 'تم التسليم', icon: CheckCircleIconSolid },
      completed: { color: 'green', text: 'مكتمل', icon: HandThumbUpIcon },
      cancelled: { color: 'red', text: 'ملغي', icon: XCircleIcon },
      refunded: { color: 'gray', text: 'مُسترد', icon: ArrowPathIcon }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-4 h-4 ml-1" />
        {config.text}
      </span>
    );
  };

  // Get action text
  const getActionText = (type) => {
    switch (type) {
      case 'received':
        return 'تأكيد استلام المنتج من البائع';
      case 'in_transit':
        return 'تحديث حالة الشحن';
      case 'delivered':
        return 'تأكيد التسليم للمشتري';
      default:
        return '';
    }
  };

    // Render order section
  const renderOrderSection = (title, orders, icon, iconColor, actionType, emptyMessage) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200"
    >
      <div className={`px-6 py-4 border-b border-gray-200 bg-gradient-to-r ${iconColor} rounded-t-xl`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {icon}
            <h3 className="text-lg font-semibold text-white mr-2">{title}</h3>
            <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm mr-3">
              {orders.length}
      </span>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">{emptyMessage}</p>
          </div>
        ) : (
          orders.map((order) => (
            <motion.div
              key={order._id}
              whileHover={{ backgroundColor: '#f9fafb' }}
              className="p-6 cursor-pointer"
              onClick={() => handleViewOrder(order)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {order.orderNumber}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(order.createdAt).toLocaleDateString('ar-SA')}
                      </p>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                  
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">المشتري</p>
                      <p className="text-sm font-medium text-gray-900">{order.buyer?.lab?.labName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">البائع</p>
                      <p className="text-sm font-medium text-gray-900">{order.seller?.lab?.labName}</p>
                    </div>
                  </div>

                  <div className="mt-2">
                    <p className="text-xs text-gray-500">المنتجات</p>
                    <p className="text-sm text-gray-700">
                      {order.items?.map(item => `${item.productName || item.name} (${item.quantity})`).join(', ')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 space-x-reverse mr-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOrder(order);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="عرض التفاصيل"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  {actionType && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAction(order, actionType);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={getActionText(actionType)}
                    >
                      <CheckCircleIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </motion.div>
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ShoppingBagIcon className="h-8 w-8 ml-3 text-blue-600" />
                إدارة الطلبات الجديدة
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                إدارة ومتابعة الطلبات الجديدة عبر مراحل الاستلام والشحن والتسليم
              </p>
            </div>
            <button
              onClick={loadOrdersByStatus}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <ArrowPathIcon className="h-5 w-5 ml-2" />
              تحديث
            </button>
          </div>
        </div>
      </div>

      {/* Order Sections */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        {/* Orders waiting for pickup */}
        {renderOrderSection(
          'في انتظار الاستلام من البائع',
          ordersByStatus.paidOrders || [],
          <ClockIconSolid className="h-6 w-6 text-white" />,
          'from-brand-blue to-brand-blueSecondary',
          'received',
          'لا توجد طلبات في انتظار الاستلام'
        )}

        {/* Orders received - ready for shipping */}
        {renderOrderSection(
          'تم الاستلام - جاري التحضير للشحن',
          ordersByStatus.receivedOrders || [],
          <ClipboardCheckSolid className="h-6 w-6 text-white" />,
          'from-brand-blue to-brand-blueSecondary',
          'in_transit',
          'لا توجد طلبات جاهزة للشحن'
        )}

        {/* Orders in transit */}
        {renderOrderSection(
          'في الطريق للمشتري',
          ordersByStatus.inTransitOrders || [],
          <TruckIconSolid className="h-6 w-6 text-white" />,
          'from-brand-blue to-brand-blueSecondary',
          'delivered',
          'لا توجد طلبات في الطريق'
        )}
      </motion.div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center">
                        <ShoppingBagIconSolid className="h-6 w-6 ml-2" />
                      تفاصيل الطلب
                    </h2>
                    <p className="text-blue-100 mt-1">{selectedOrder.orderNumber}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <InformationCircleIcon className="h-5 w-5 ml-2 text-blue-600" />
                      معلومات أساسية
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم الطلب:</span>
                        <span className="font-medium">{selectedOrder.orderNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">الحالة:</span>
                        {getStatusBadge(selectedOrder.status)}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">تاريخ الإنشاء:</span>
                        <span className="font-medium">
                          {new Date(selectedOrder.createdAt).toLocaleDateString('ar-SA')}
                        </span>
                      </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">المبلغ الإجمالي:</span>
                          <span className="font-medium text-green-600">
                          {selectedOrder.totalAmount?.toLocaleString()} ريال
                          </span>
                        </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <TruckIcon className="h-5 w-5 ml-2 text-blue-600" />
                      معلومات الشحن
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">شركة الشحن:</span>
                        <span className="font-medium">
                          {selectedOrder.shipping?.carrier ? 
                            shippingCompanies.find(c => c.id === selectedOrder.shipping.carrier)?.name || selectedOrder.shipping.carrier
                            : 'غير محدد'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم التتبع:</span>
                        <span className="font-medium">
                          {selectedOrder.shipping?.trackingNumber || 'غير محدد'}
                        </span>
                      </div>
                      {selectedOrder.shipping?.pickupCompletedAt && (
                      <div className="flex justify-between">
                          <span className="text-gray-600">تاريخ الاستلام:</span>
                        <span className="font-medium">
                            {new Date(selectedOrder.shipping.pickupCompletedAt).toLocaleDateString('ar-SA')}
                        </span>
                      </div>
                      )}
                      {selectedOrder.shipping?.deliveryCompletedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">تاريخ التسليم:</span>
                          <span className="font-medium">
                            {new Date(selectedOrder.shipping.deliveryCompletedAt).toLocaleDateString('ar-SA')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Parties Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <UserIcon className="h-5 w-5 ml-2 text-green-600" />
                      المشتري
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center">
                        <BuildingStorefrontIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span className="font-medium">{selectedOrder.buyer?.lab?.labName}</span>
                      </div>
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span>{selectedOrder.buyer?.user?.name}</span>
                      </div>
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span>{selectedOrder.buyer?.user?.email}</span>
                      </div>
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span>{selectedOrder.buyer?.user?.phone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <BuildingStorefrontIcon className="h-5 w-5 ml-2 text-blue-600" />
                      البائع
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center">
                        <BuildingStorefrontIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span className="font-medium">{selectedOrder.seller?.lab?.labName}</span>
                      </div>
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span>{selectedOrder.seller?.user?.name}</span>
                      </div>
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span>{selectedOrder.seller?.user?.email}</span>
                      </div>
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 ml-2 text-gray-400" />
                        <span>{selectedOrder.seller?.user?.phone}</span>
                      </div>
                          </div>
                  </div>
                </div>

                {/* Pickup Address (Seller) */}
                {selectedOrder.seller?.lab && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MapPinIcon className="h-5 w-5 ml-2 text-emerald-600" />
                      عنوان الاستلام (البائع)
                    </h3>
                    <div className="bg-emerald-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center">
                          <BuildingStorefrontIcon className="h-4 w-4 ml-2 text-gray-400" />
                          <span className="font-medium">{selectedOrder.seller.lab.labName}</span>
                        </div>
                        {selectedOrder.seller?.user?.phone && (
                          <div className="flex items-center">
                            <PhoneIcon className="h-4 w-4 ml-2 text-gray-400" />
                            <span>{selectedOrder.seller.user.phone}</span>
                        </div>
                      )}
                        {selectedOrder.seller?.user?.email && (
                          <div className="flex items-center">
                            <EnvelopeIcon className="h-4 w-4 ml-2 text-gray-400" />
                            <span>{selectedOrder.seller.user.email}</span>
                    </div>
                        )}
                  </div>
                      <div className="mt-1">
                        <span className="text-gray-600">العنوان:</span>
                        <p className="text-gray-800 mt-1">{selectedOrder.seller.lab.address}</p>
                </div>
                    </div>
                  </div>
                )}

                {/* Delivery Address (Buyer) */}
                {selectedOrder.shippingAddress && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MapPinIcon className="h-5 w-5 ml-2 text-amber-600" />
                      عنوان التسليم (المشتري)
                    </h3>
                    <div className="bg-amber-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">الاسم الكامل:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.fullName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">الهاتف:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.phone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">المدينة:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.city}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">المنطقة:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.region}</span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <span className="text-gray-600">العنوان الكامل:</span>
                        <p className="text-gray-800 mt-1">{selectedOrder.shippingAddress.address}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Items */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 ml-2 text-indigo-600" />
                    المنتجات
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      {selectedOrder.items?.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                          <div className="flex items-center space-x-3 space-x-reverse">
                            {item.productImage && (
                              <img
                                src={getImageUrl(item.productImage)}
                                alt={item.productName}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            )}
                            <div>
                              <h4 className="font-medium text-gray-900">{item.productName}</h4>
                              <p className="text-sm text-gray-500">الكمية: {item.quantity}</p>
                            </div>
                          </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">
                              {item.total?.toLocaleString()} ريال
                              </p>
                              <p className="text-sm text-gray-500">
                                {item.price?.toLocaleString()} × {item.quantity}
                              </p>
                            </div>
                        </div>
                      ))}
                    </div>
                  </div>
                        </div>

                {/* Status History */}
                {selectedOrder.statusHistory && selectedOrder.statusHistory.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 ml-2 text-purple-600" />
                      سجل الحالات
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-2">
                        {selectedOrder.statusHistory.map((history, index) => (
                          <div key={index} className="flex items-center justify-between p-2">
                            <div className="flex items-center space-x-2 space-x-reverse">
                              {getStatusBadge(history.status)}
                              <span className="text-sm text-gray-600">
                                {new Date(history.updatedAt).toLocaleString('ar-SA')}
                              </span>
                        </div>
                            {history.note && (
                              <span className="text-sm text-gray-500">{history.note}</span>
                            )}
                          </div>
                        ))}
                      </div>
                        </div>
                    </div>
                  )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-between">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  إغلاق
                </button>
                                <div className="flex space-x-2 space-x-reverse">
                  {!['delivered', 'completed', 'cancelled'].includes(selectedOrder.status) && (
                  <button
                      onClick={() => handleCancelOrder(selectedOrder)}
                      disabled={cancellingOrder === selectedOrder._id}
                      className={`px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg transition-colors flex items-center ${
                        cancellingOrder === selectedOrder._id 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {cancellingOrder === selectedOrder._id ? (
                        <ArrowPathIcon className="h-4 w-4 ml-2 animate-spin" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 ml-2" />
                      )}
                      {cancellingOrder === selectedOrder._id ? 'جاري الإلغاء...' : 'إلغاء الطلب'}
                  </button>
                )}
                  {selectedOrder.status === 'paid' && (
                  <button
                      onClick={() => handleAction(selectedOrder, 'received')}
                      disabled={processingAction}
                      className={`px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg transition-colors flex items-center ${
                        processingAction ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-95'
                      }`}
                    >
                      <ClipboardDocumentCheckIcon className="h-4 w-4 ml-2" />
                      تأكيد الاستلام
                    </button>
                  )}
                  {selectedOrder.status === 'admin_received' && (
                    <button
                      onClick={() => handleAction(selectedOrder, 'in_transit')}
                      disabled={processingAction}
                      className={`px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg transition-colors flex items-center ${
                        processingAction ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-95'
                      }`}
                  >
                    <TruckIcon className="h-4 w-4 ml-2" />
                      بدء الشحن
                    </button>
                  )}
                  {selectedOrder.status === 'in_transit' && (
                    <button
                      onClick={() => handleAction(selectedOrder, 'delivered')}
                      disabled={processingAction}
                      className={`px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg transition-colors flex items-center ${
                        processingAction ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-95'
                      }`}
                    >
                      <CheckCircleIcon className="h-4 w-4 ml-2" />
                      تأكيد التسليم
                  </button>
                )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Modal */}
      <AnimatePresence>
        {showActionModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => !processingAction && setShowActionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{getActionText(actionType)}</h2>
                    <p className="text-blue-100 text-sm mt-1">{selectedOrder.orderNumber}</p>
                  </div>
                  <button
                    onClick={() => !processingAction && setShowActionModal(false)}
                    disabled={processingAction}
                    className={`text-white transition-colors ${
                      processingAction ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-200'
                    }`}
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {actionType === 'received' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 text-blue-600 ml-2 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-800">
                        بتأكيد الاستلام، سيتم إشعار البائع بأنه تم استلام المنتج وسيتم إضافة المبلغ إلى محفظته
                      </p>
                    </div>
                  </div>
                )}

                {actionType === 'in_transit' && (
                  <>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 text-purple-600 ml-2 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-purple-800">
                          اختر شركة الشحن وأدخل رقم التتبع لتمكين المشتري من متابعة الطلب
                        </p>
                </div>
                    </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    شركة الشحن *
                  </label>
                  <select
                    value={actionData.shippingCompany}
                    onChange={(e) => setActionData(prev => ({ ...prev, shippingCompany: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">اختر شركة الشحن</option>
                    {shippingCompanies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.nameEn})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                        رقم التتبع
                  </label>
                  <input
                    type="text"
                        value={actionData.trackingNumber}
                        onChange={(e) => setActionData(prev => ({ ...prev, trackingNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="أدخل رقم التتبع"
                  />
                </div>
                  </>
                )}

                {actionType === 'delivered' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 text-green-600 ml-2 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-green-800">
                        بتأكيد التسليم، سيتم إشعار المشتري بأنه تم تسليم الطلب بنجاح
                      </p>
                </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ملاحظات (اختياري)
                  </label>
                  <textarea
                    value={actionData.notes}
                    onChange={(e) => setActionData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="أضف أي ملاحظات..."
                  />
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3 space-x-reverse">
                <button
                  onClick={() => setShowActionModal(false)}
                  disabled={processingAction}
                  className={`px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors ${
                    processingAction ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                  }`}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleActionSubmit}
                  disabled={processingAction}
                  className={`px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg transition-colors flex items-center ${
                    processingAction ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-95'
                  }`}
                >
                  {processingAction ? (
                    <ArrowPathIcon className="h-4 w-4 ml-2 animate-spin" />
                  ) : (
                  <CheckCircleIcon className="h-4 w-4 ml-2" />
                  )}
                  {processingAction ? 'جاري المعالجة...' : 'تأكيد'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminOrders;
